import { useEffect, useRef, useState } from 'react';
import { computeDailyBudget, type DailyBudgetInput } from '../../lib/dailyBudget';
import { centsToDisplay } from '../../lib/money';
import { useClickOutside } from '../../lib/useClickOutside';
import type { Currency } from '../../types/account';
import {
  buildBudgetModifiedContent,
  reducePopoverOpen,
  type PopoverOpen,
} from './budgetModifiedPopover';

type Props = {
  row: DailyBudgetInput;
  platform: 'meta' | 'google';
  currency: Currency;
  // When true, render a small amber ⚠ button after the daily value. Click
  // toggles a popover showing Account default vs row's current values for
  // that platform. Computed upstream via src/lib/dailyBudget.ts#isBudgetModified.
  // No effect on the error branch (missing_dates / invalid_range / no_budget).
  modified?: boolean;
  // Account.{platform}MasterMonthlyBudget in cents. Required for the
  // "modified" popover's "Account default" line. Optional because not every
  // call site has Account context wired through (master grid + row editor
  // do; future surfaces can opt in). When absent, the popover shows "—".
  accountMonthlyBudget?: number | null;
  // Pre-P2b-3 §2/§3: render the cell as disabled when the platform is
  // effectively off for this row (Account.runsMeta=false, or row override
  // forces off). Suppresses the daily value, the breakdown click, and the
  // modified ⚠. computeDailyBudget stays pure-numeric; gating happens here.
  disabled?: boolean;
};

// Master-view cell (spec §7.1 columns 10 + 13). Renders $X.XX/day or "—" with
// a warning icon when the computation errors. Click the value opens a
// breakdown popover (monthly/30 + increase/campaignDays). Click the modified
// ⚠ opens a separate popover with Account default vs row's current values.
// The two popovers are mutually exclusive — clicking one while the other is
// open just swaps.
//
// Reused by P2b-3's Meta + Google grids (columns 5 on both) and by the row
// editor's Budget section.
export function DailyBudgetCell({
  row,
  platform,
  currency,
  modified = false,
  accountMonthlyBudget,
  disabled = false,
}: Props) {
  const [open, setOpen] = useState<PopoverOpen>(null);
  const rootRef = useRef<HTMLSpanElement>(null);
  useClickOutside([rootRef], () => setOpen(null), open !== null);

  // Escape closes whichever popover is currently open. Mirrors the modal
  // dismissal pattern used elsewhere (RowHistoryModal etc.) so the
  // keyboard contract is consistent.
  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Disabled (platform effectively off) wins over every other render branch.
  // The cell is read-only and visually muted; popovers don't open here.
  if (disabled) {
    return <span className="text-slate-300 select-none">—</span>;
  }

  const result = computeDailyBudget(row, platform);

  if ('error' in result) {
    const title =
      result.error === 'missing_dates'
        ? 'Start or end date is missing.'
        : result.error === 'invalid_range'
          ? 'End date must be after start date.'
          : 'No budget configured for this platform.';
    return (
      <span className="inline-flex items-center gap-1 text-slate-400" title={title}>
        —<span className="text-amber-500" aria-hidden>⚠</span>
      </span>
    );
  }

  const rowMonthlyBudget =
    platform === 'meta' ? row.metaMonthlyBudget : row.googleMonthlyBudget;
  const rowIncrease =
    platform === 'meta' ? row.metaCampaignIncrease : row.googleCampaignIncrease;
  const modifiedContent = modified
    ? buildBudgetModifiedContent({
        platform,
        currency,
        accountMonthlyBudget: accountMonthlyBudget ?? null,
        rowMonthlyBudget,
        rowIncrease,
      })
    : null;

  return (
    <span ref={rootRef} className="relative inline-block">
      <button
        type="button"
        className="text-slate-900 hover:underline"
        onClick={() => setOpen((prev) => reducePopoverOpen(prev, 'breakdown'))}
        title="Click for breakdown"
      >
        {centsToDisplay(result.perDayCents, currency)}/day
      </button>
      {modifiedContent && (
        <button
          type="button"
          aria-label={modifiedContent.title}
          aria-expanded={open === 'modified'}
          className="ml-1 text-amber-500 hover:text-amber-600 cursor-pointer"
          onClick={() => setOpen((prev) => reducePopoverOpen(prev, 'modified'))}
        >
          ⚠
        </button>
      )}
      {open === 'breakdown' && (
        <span
          className="absolute z-10 right-0 mt-1 w-72 rounded-md border border-slate-300 bg-white p-3 shadow-lg text-xs font-mono"
          role="tooltip"
        >
          <button
            type="button"
            onClick={() => setOpen(null)}
            aria-label="Dismiss"
            className="absolute top-1 right-1 w-5 h-5 inline-flex items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 text-xs"
          >
            ✕
          </button>
          <div className="flex justify-between pr-5">
            <span>Monthly {centsToDisplay(rowMonthlyBudget, currency)} / 30 days</span>
            <span>= {centsToDisplay(result.monthlyPerDayCents, currency)}/day</span>
          </div>
          <div className="flex justify-between">
            <span>
              Increase {centsToDisplay(rowIncrease, currency)} / {result.campaignDays}d
            </span>
            <span>= {centsToDisplay(result.increasePerDayCents, currency)}/day</span>
          </div>
          <div className="mt-2 border-t border-slate-300 pt-2 flex justify-between font-semibold">
            <span>Daily total:</span>
            <span>{centsToDisplay(result.perDayCents, currency)}/day</span>
          </div>
        </span>
      )}
      {open === 'modified' && modifiedContent && (
        <span
          className="absolute z-10 right-0 mt-1 w-72 rounded-md border border-slate-300 bg-white p-3 shadow-lg text-xs"
          role="tooltip"
        >
          <button
            type="button"
            onClick={() => setOpen(null)}
            aria-label="Dismiss"
            className="absolute top-1 right-1 w-5 h-5 inline-flex items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 text-xs"
          >
            ✕
          </button>
          <div className="font-semibold text-slate-900 mb-2 pr-5">
            {modifiedContent.title}
          </div>
          {/* "Account default" is the reference value, "This row" is the
              contextual focus on the rollout. Reference reads muted; the
              row's value reads at the cell's normal weight. Same column
              alignment so the eye scans the dollar amounts together. */}
          <dl className="space-y-1">
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Account default:</dt>
              <dd className="font-mono text-slate-500">
                {modifiedContent.accountLine}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-700 font-medium">This row:</dt>
              <dd className="font-mono text-slate-900 font-medium">
                {modifiedContent.rowLine}
              </dd>
            </div>
            <div className="flex justify-between gap-4 pt-1 border-t border-slate-200">
              <dt className="text-slate-500">Increase:</dt>
              <dd className="font-mono text-slate-700">
                {modifiedContent.increaseLine}
              </dd>
            </div>
          </dl>
        </span>
      )}
    </span>
  );
}
